import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Broadcast, Plus, Trash, MapPin, ArrowRight, Buildings, Sparkle } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import AddressAutocomplete from "@/components/AddressAutocomplete";

export default function AdminWalkRadar() {
  const [routes, setRoutes] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [stops, setStops] = useState([{ address: "", unit: "", label: "" }]);
  const [landlordEmail, setLandlordEmail] = useState("");
  const [landlordName, setLandlordName] = useState("");
  const [landlords, setLandlords] = useState([]);
  const [selectedLandlordId, setSelectedLandlordId] = useState("");
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try { const r = await api.get("/walk-routes"); setRoutes(r.data.routes || []); }
    catch { setRoutes([]); }
  };
  const loadLandlords = async () => {
    try { const r = await api.get("/landlords"); setLandlords(r.data || []); }
    catch { setLandlords([]); }
  };
  useEffect(() => { load(); loadLandlords(); }, []);

  const addStop = () => setStops([...stops, { address: "", unit: "", label: "" }]);
  const removeStop = (i) => setStops(stops.filter((_, idx) => idx !== i));
  const updateStop = (i, k, v) => setStops(stops.map((s, idx) => idx === i ? { ...s, [k]: v } : s));

  const pickLandlord = async (llId) => {
    setSelectedLandlordId(llId);
    if (!llId) return;
    setLoadingPortfolio(true);
    try {
      const { data } = await api.get(`/landlords/${llId}/addresses`);
      if (data.count === 0) {
        toast.info(`${data.landlord.name} has no linked properties yet — link some in Landlords first.`);
      } else {
        setStops(data.stops.length ? data.stops : [{ address: "", unit: "", label: "" }]);
        setLandlordName(data.landlord.name || "");
        setLandlordEmail(data.landlord.email || "");
        if (!name.trim()) setName(`${data.landlord.name} · Portfolio Walk`);
        toast.success(`Loaded ${data.count} propert${data.count === 1 ? "y" : "ies"} for ${data.landlord.name}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load portfolio");
    } finally {
      setLoadingPortfolio(false);
    }
  };

  const create = async () => {
    const valid = stops.filter(s => s.address.trim());
    if (!name.trim() || !valid.length) { toast.error("Route name + at least one stop"); return; }
    setCreating(true);
    try {
      const r = await api.post("/walk-routes", {
        name: name.trim(), stops: valid, landlord_email: landlordEmail.trim() || undefined,
        landlord_name: landlordName.trim(), nearest_neighbor: true,
      });
      toast.success(`Route created · ${r.data.stops?.length || 0} stops`);
      nav(`/walk/${r.data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed"); }
    finally { setCreating(false); }
  };

  return (
    <AdminShell>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="walk-radar-page">
        <div className="flex items-center gap-3">
          <Broadcast weight="fill" size={30} className="text-sky-600"/>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">WalkRadar™</h1>
        </div>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          Build a walking route for a portfolio walk-through. Nearest-neighbor auto-sorts the stops, tech rates each one, and the landlord/investor gets a branded email report the moment you finish.
        </p>

        <section className="mt-6 rugged-card p-4" data-testid="walk-radar-create">
          <h2 className="font-display font-black uppercase tracking-widest text-sm mb-3">New Walk</h2>

          <div className="mb-3 p-3 border-2 border-dashed border-[#00C4CC] bg-cyan-50/40" data-testid="walk-portfolio-picker">
            <div className="flex items-center gap-2 mb-2">
              <Sparkle weight="fill" size={14} className="text-[#00C4CC]"/>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">Auto-load a landlord's portfolio</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={selectedLandlordId}
                onChange={e => pickLandlord(e.target.value)}
                disabled={loadingPortfolio}
                data-testid="walk-landlord-picker"
                className="flex-1 h-11 px-3 border-2 border-[#0f172a] text-sm bg-white font-medium disabled:opacity-50"
              >
                <option value="">— Pick a landlord (or add stops manually below) —</option>
                {landlords.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.name}{l.property_count ? ` · ${l.property_count} propert${l.property_count === 1 ? "y" : "ies"}` : " · no properties linked"}
                  </option>
                ))}
              </select>
              {selectedLandlordId && (
                <button
                  type="button"
                  onClick={() => { setSelectedLandlordId(""); setStops([{ address: "", unit: "", label: "" }]); setLandlordName(""); setLandlordEmail(""); }}
                  data-testid="walk-portfolio-clear"
                  className="h-11 px-4 border-2 border-slate-400 text-slate-600 text-[11px] font-black uppercase tracking-widest hover:bg-slate-50"
                >
                  Clear
                </button>
              )}
            </div>
            {loadingPortfolio && <div className="mt-2 text-[11px] text-slate-500 font-bold uppercase tracking-widest">Loading portfolio…</div>}
          </div>

          <input value={name} onChange={e => setName(e.target.value)} placeholder="Route name (e.g. Rittenhouse Portfolio · Q3 Walk)"
            data-testid="walk-name" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm mb-2"/>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <input value={landlordName} onChange={e => setLandlordName(e.target.value)} placeholder="Landlord / investor name"
              data-testid="walk-landlord-name" className="h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            <input value={landlordEmail} onChange={e => setLandlordEmail(e.target.value)} placeholder="Their email (auto-emailed on complete)"
              data-testid="walk-landlord-email" className="h-11 px-3 border-2 border-[#0f172a] text-sm"/>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Stops · {stops.filter(s => s.address.trim()).length} filled</span>
            <button onClick={addStop} data-testid="walk-add-stop"
              className="h-8 px-3 border-2 border-dashed border-slate-400 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 inline-flex items-center gap-1">
              <Plus size={11}/> Add Stop
            </button>
          </div>
          <div className="space-y-2">
            {stops.map((s, i) => (
              <div key={i} className="flex gap-2 items-start" data-testid={`walk-stop-${i}`}>
                <span className="pt-3 font-black text-slate-400 w-6 text-center">{i+1}</span>
                <div className="flex-1">
                  <AddressAutocomplete
                    value={s.address}
                    onChange={(v) => updateStop(i, "address", v)}
                    onSelect={({ label }) => updateStop(i, "address", label)}
                    placeholder="Start typing an address…"
                    testId={`walk-stop-${i}-addr`}
                  />
                </div>
                <input value={s.unit} onChange={e => updateStop(i, "unit", e.target.value)} placeholder="Unit"
                  data-testid={`walk-stop-${i}-unit`}
                  className="w-20 h-11 px-2 border-2 border-slate-300 text-sm mt-6"/>
                <input value={s.label} onChange={e => updateStop(i, "label", e.target.value)} placeholder="Label"
                  data-testid={`walk-stop-${i}-label`}
                  className="w-32 h-11 px-2 border-2 border-slate-300 text-sm mt-6"/>
                <button onClick={() => removeStop(i)}
                  aria-label="Remove stop"
                  data-testid={`walk-stop-${i}-remove`}
                  className="w-11 h-11 border-2 border-red-300 text-red-600 hover:bg-red-50 mt-6 flex items-center justify-center">
                  <Trash size={14}/>
                </button>
              </div>
            ))}
          </div>
          <button onClick={create} disabled={creating} data-testid="walk-create-btn"
            className="mt-4 h-11 px-5 bg-sky-600 text-white text-[11px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-2">
            {creating ? "Creating…" : "Create & Start Walk"} <ArrowRight size={12} weight="bold"/>
          </button>
        </section>

        <section className="mt-6" data-testid="walk-radar-list">
          <h2 className="font-display font-black uppercase tracking-widest text-sm mb-3">Recent Walks</h2>
          {!routes && <div className="text-center text-slate-500 font-bold uppercase text-xs">Loading…</div>}
          {routes?.length === 0 && <div className="text-slate-500 text-sm italic">No walks yet.</div>}
          <div className="space-y-2">
            {routes?.map(r => (
              <div key={r.id} data-testid={`walk-row-${r.id}`}
                onClick={() => nav(`/walk/${r.id}`)}
                className="rugged-card p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50">
                <MapPin weight="fill" size={20} className={r.status === "completed" ? "text-emerald-600" : "text-sky-600"}/>
                <div className="flex-1">
                  <div className="font-black">{r.name}</div>
                  <div className="text-xs text-slate-500">
                    {r.stops?.length || 0} stops · {r.status}
                    {r.landlord_name && ` · ${r.landlord_name}`}
                  </div>
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {r.completed_at ? new Date(r.completed_at).toLocaleDateString() : (r.started_at ? "in progress" : "pending")}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </AdminShell>
  );
}
